import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import CustomDialog from 'components/CustomDialog/customDialog';
import {
  CreateRequestDialogHeader,
  TaskFormCnt,
} from 'components/AllDialogs/requests/createRequest/createRequest.style';
import CreateRequestInput from 'components/AllDialogs/requests/createRequest/createRequestInput.cmp';
import TaskPriorityDropdown from 'components/AllDialogs/requests/createRequest/priorityDropdown.cmp';
import TaskCategoryDropdown from 'components/AllDialogs/requests/createRequest/categoryDropdown.cmp';
import TaskDescriptionInput from 'components/AllDialogs/requests/createRequest/requestDescriptionInput.cmp';
import TaskAttachment from 'components/AllDialogs/requests/createRequest/taskAttachment.cmp';
import { useDispatch, useSelector } from 'react-redux';
import { getDialogState } from 'store/selectors/dialogSelectors';
import { setDialog } from 'store/actions/dialogActions';
import { VALIDATION_ERROR_MESSAGES } from 'utils/globalVariable';
import xeniaApi from 'api/index';
import TaskLocationDropdown from 'components/AllDialogs/requests/createRequest/locationDropdown.cmp';
import classNames from 'classnames';
import actions from 'store/actions';
import useDateTime from 'utils/CustomHooks/useDateTime';
import { useTaskEdit } from 'controller/useTaskEdit';
import TaskAssetDropdown from 'components/AllDialogs/requests/createRequest/assetDropdown.cmp';
import AssetInfo from 'components/AllDialogs/requests/createRequest/assetsInfo.cmp';
import { isEmpty, isEqual } from 'lodash';
import useCreateRequest from 'utils/CustomHooks/useCreateRequest';
import { CreateRequestContext } from 'components/AllDialogs/requests/createRequest/context/context';
import DIALOGS from 'utils/dialogIds';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { FormHelperText, Grid, Stack, Typography } from '@mui/material';
import CrossIcon from 'components/Icons/crossIcon';
import { getHotelId } from 'utils/globalFunction';

const CreateRequestHeader = ({ closeAction, isTitleEnabled }) => {
  return (
    <CreateRequestDialogHeader>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        className="titleCnt"
      >
        <Typography className="title">Create New Request</Typography>
        <CrossIcon className="icon" onClick={closeAction} />
      </Stack>
      {isTitleEnabled && <CreateRequestInput />}
    </CreateRequestDialogHeader>
  );
};

function CreateRequest(props) {
  const { createRequest } = useCreateRequest();
  const [newRequest, setNewRequest] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [uploadsChange, setUploadsChange] = useState<boolean>(false);
  const [taskTimeChange, setTaskTimeChange] = useState<boolean>(false);
  const [taskTime, setTaskTime] = useState<any>({ startTime: '', dueTime: '' });
  const [showRepeatTask, setShowRepeatTask] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<[]>([]);
  const theme = useTheme();
  const isMobileDevice = useMediaQuery(theme.breakpoints.down('md'));

  const [config, setConfig] = useState<any>(null);
  const hotelId = getHotelId() as string;

  const { handleUpdateTask } = useTaskEdit(newRequest);

  const [submitButtonLoading, setSubmitButtonLoading] =
    useState<boolean>(false);

  const dialogState = useSelector(getDialogState);
  const initialData = useRef(
    dialogState?.[DIALOGS.CREATE_EDIT_REQUEST]?.data?.requestData,
  );

  const dispatch = useDispatch();

  //Function to reset task form
  const resetTaskForm = () => {
    setNewRequestObj({});
    setErrors({});
    setShowRepeatTask(false);
    setAttachments([]);
  };

  //Reset task form on unmount
  useEffect(() => {
    return () => {
      resetTaskForm();
    };
  }, []);

  // Create Task
  const createNewRequest = async (data = {}) => {
    const postObj = {
      ...newRequest,
      ...data,
    };

    const requestResponse = await createRequest(postObj);
    if (requestResponse) {
      dialogState?.[DIALOGS.CREATE_EDIT_REQUEST]?.data?.onSuccessCallBack?.(
        requestResponse,
      );
      dispatch(
        setDialog({
          open: false,
          dialogId: DIALOGS.CREATE_EDIT_REQUEST,
        }),
      );
    } else {
      setSubmitButtonLoading(false);
    }
  };

  const editTask = async (data = {}) => {
    const { AssignId, title, checklistId } = newRequest;

    //In case of show less only minimized version of task should be created, other selected values will not be considered
    const postObj =
      // showLess
      // ? {
      //     ...(AssignId && { AssignId }),
      //     title,
      //     editInstance: editSeriesType,
      //     ...(checklistId && { checklistId }),
      //   }
      // :
      {
        ...newRequest,
        ...data,
      };

    const taskResponse = await handleUpdateTask(postObj);
    if (taskResponse) {
      dialogState?.[DIALOGS.CREATE_EDIT_REQUEST]?.data?.onSuccessCallBack?.();
      dispatch(actions.bulkUpdateTasks(taskResponse?.data));
      dispatch(
        setDialog({
          open: false,
          dialogId: 'createEditTask',
        }),
      );
    } else {
      setSubmitButtonLoading(false);
    }
  };

  //Task creation with attachments
  const createEditRequestWithAttachment = async () => {
    const response = await xeniaApi.CreateTaskAttchmentFilesApi(attachments);
    if (response) {
      const attachedList = editing
        ? [
            ...newRequest.attachment,
            ...response.filePath.map((item) => response.baseUrl + item),
          ]
        : response?.filePath;

      editing
        ? editTask({ attachment: attachedList })
        : createNewRequest({ attachments: attachedList });
    }
  };
  //Function passed to the context to update the root state of new create task
  const updateRequest: any = (value) => {
    setNewRequest({ ...newRequest, ...value });
  };
  //Function passed to the context to replace the new task object in state
  const setNewRequestObj: any = (value) => {
    setNewRequest(value);
  };

  const { fields, isSyncEnabled } = config || {};

  const isTitleEnabled = useMemo(
    () => !isSyncEnabled || (isSyncEnabled && fields?.title?.show),
    [isSyncEnabled, fields],
  );

  const isTitleRequired = useMemo(
    () => !isSyncEnabled || (isSyncEnabled && fields?.title?.required),
    [isSyncEnabled, fields],
  );

  const isPriorityEnabled = useMemo(
    () => !isSyncEnabled || (isSyncEnabled && fields?.priority?.show),
    [isSyncEnabled, fields],
  );

  const isPriorityRequired = useMemo(
    () => isSyncEnabled && fields?.priority?.required,
    [isSyncEnabled, fields],
  );

  const isCategoryEnabled = useMemo(
    () => !isSyncEnabled || (isSyncEnabled && fields?.CategoryId?.show),
    [isSyncEnabled, fields],
  );

  const isCategoryRequired = useMemo(
    () => isSyncEnabled && fields?.CategoryId?.required,
    [isSyncEnabled, fields],
  );

  const isLocationEnabled = useMemo(
    () => !isSyncEnabled || (isSyncEnabled && fields?.LocationId?.show),
    [isSyncEnabled, fields],
  );

  const isLocationRequired = useMemo(
    () => isSyncEnabled && fields?.LocationId?.required,
    [isSyncEnabled, fields],
  );

  const isAssetEnabled = useMemo(
    () => !isSyncEnabled || (isSyncEnabled && fields?.AssetId?.show),
    [isSyncEnabled, fields],
  );

  const isAssetRequired = useMemo(
    () => isSyncEnabled && fields?.AssetId?.required,
    [isSyncEnabled, fields],
  );

  const isDescriptionEnabled = useMemo(
    () => !isSyncEnabled || (isSyncEnabled && fields?.description?.show),
    [isSyncEnabled, fields],
  );

  const isDescriptionRequired = useMemo(
    () => isSyncEnabled && fields?.description?.required,
    [isSyncEnabled, fields],
  );

  const isAttachmentEnabled = useMemo(
    () => !isSyncEnabled || (isSyncEnabled && fields?.attachments?.show),
    [isSyncEnabled, fields],
  );

  const isAttachmentRequired = useMemo(
    () => isSyncEnabled && fields?.attachments?.required,
    [isSyncEnabled, fields],
  );

  //Submit create form
  const handleRequestFormSubmit = () => {
    //Title field is required, show error if title field is empty
    if (isTitleRequired && !newRequest.title) {
      setErrors({
        ...errors,
        title: VALIDATION_ERROR_MESSAGES.REQUEST_TITLE_REQUIRED,
      });
      return;
    }

    if (isPriorityRequired && !newRequest.priority) {
      setErrors({
        ...errors,
        priority: VALIDATION_ERROR_MESSAGES.REQUEST_PRIORITY_REQUIRED,
      });
      return;
    }

    if (isCategoryRequired && !newRequest.CategoryId) {
      setErrors({
        ...errors,
        CategoryId: VALIDATION_ERROR_MESSAGES.REQUEST_CATEGORY_REQUIRED,
      });
      return;
    }

    if (isLocationRequired && !newRequest.LocationId) {
      setErrors({
        ...errors,
        LocationId: VALIDATION_ERROR_MESSAGES.REQUEST_LOCATION_REQUIRED,
      });
      return;
    }

    if (isAssetRequired && !newRequest.AssetId) {
      setErrors({
        ...errors,
        AssetId: VALIDATION_ERROR_MESSAGES.REQUEST_ASSET_REQUIRED,
      });
      return;
    }

    if (isDescriptionRequired && !newRequest.description) {
      setErrors({
        ...errors,
        description: VALIDATION_ERROR_MESSAGES.REQUEST_DESCRIPTION_REQUIRED,
      });
      return;
    }

    if (isAttachmentRequired && !attachments?.length) {
      setErrors({
        ...errors,
        attachments: VALIDATION_ERROR_MESSAGES.REQUEST_ATTACHMENT_REQUIRED,
      });
      return;
    }

    setSubmitButtonLoading(true);
    if (attachments?.length) {
      createEditRequestWithAttachment();
    } else {
      editing ? editTask() : createNewRequest();
    }
  };
  const closeAction = () => {
    dispatch(
      setDialog({
        open: false,
        dialogId: DIALOGS.CREATE_EDIT_REQUEST,
      }),
    );
  };

  const editing = dialogState?.[DIALOGS.CREATE_EDIT_REQUEST]?.data?.editing;
  const disableRecurringEdit = editing && newRequest?.recurringTask;

  const state: any = {
    updateRequest,
    newRequest,
    setNewRequest,
    showRepeatTask,
    setShowRepeatTask,
    attachments,
    setAttachments,
    errors,
    setErrors,
    setNewRequestObj,
    taskTime,
    setTaskTime,
    editing,
    disableRecurringEdit,
  };
  const isStateChanged =
    dialogState?.[DIALOGS.CREATE_EDIT_REQUEST]?.data?.isStateChanged;

  const getConfigDetails = async () => {
    const data = await xeniaApi.getPublicRequestConfig(hotelId);
    if (data) {
      setConfig(data.config);
    }
  };

  useEffect(() => {
    getConfigDetails();
  }, []);

  return (
    <CreateRequestContext.Provider value={state}>
      <CustomDialog
        actionButtonCntProps={{
          primaryBtnProps: {
            onClick: handleRequestFormSubmit,
            buttonLoader: submitButtonLoading,
            disabled:
              editing &&
              isEqual(initialData?.current, newRequest) &&
              !uploadsChange &&
              !isStateChanged,
          },
          secondaryBtnProps: { onClick: closeAction },
        }}
        primaryBtnText={editing ? 'Save' : 'Create'}
        header={
          <CreateRequestHeader
            closeAction={closeAction}
            isTitleEnabled={isTitleEnabled}
          />
        }
        dialogProps={{
          open: dialogState?.[DIALOGS.CREATE_EDIT_REQUEST]?.open,
          fullWidth: true,
          maxWidth: 'sm',
          fullScreen: isMobileDevice,
          onClose: closeAction,
        }}
      >
        <TaskFormCnt>
          <div className="iconBtnsDropdownCnt">
            {isPriorityEnabled && <TaskPriorityDropdown />}
            {isCategoryEnabled && <TaskCategoryDropdown />}
          </div>
          {errors?.priority && (
            <FormHelperText style={{ color: '#d32f2f' }}>
              {errors?.priority}
            </FormHelperText>
          )}
          {errors?.CategoryId && (
            <FormHelperText style={{ color: '#d32f2f' }}>
              {errors?.CategoryId}
            </FormHelperText>
          )}
          <div
            className={classNames({
              selectBtnsDropdownCnt: true,
            })}
          >
            <Grid container spacing={2}>
              {isLocationEnabled && (
                <Grid item xs={12} sm={6}>
                  <TaskLocationDropdown />
                  {errors?.LocationId && (
                    <FormHelperText style={{ color: '#d32f2f' }}>
                      {errors?.LocationId}
                    </FormHelperText>
                  )}
                </Grid>
              )}

              {isAssetEnabled && (
                <Grid item xs={12} sm={6}>
                  <TaskAssetDropdown />
                  {errors?.AssetId && (
                    <FormHelperText style={{ color: '#d32f2f' }}>
                      {errors?.AssetId}
                    </FormHelperText>
                  )}
                </Grid>
              )}
            </Grid>
          </div>
          <div
            className={classNames({
              iconBtnsDropdownCnt: true,
            })}
          >
            {isAssetEnabled &&
            !newRequest?.LocationId &&
            newRequest?.AssetId ? (
              <AssetInfo />
            ) : null}
          </div>
          <div>
            {isDescriptionEnabled && <TaskDescriptionInput />}
            {errors?.description && (
              <FormHelperText style={{ color: '#d32f2f' }}>
                {errors?.description}
              </FormHelperText>
            )}
            {isAttachmentEnabled && (
              <TaskAttachment setUploadsChange={setUploadsChange} />
            )}
            {errors?.attachments && (
              <FormHelperText style={{ color: '#d32f2f', marginTop: '-12px' }}>
                {errors?.attachments}
              </FormHelperText>
            )}
          </div>
        </TaskFormCnt>
      </CustomDialog>
    </CreateRequestContext.Provider>
  );
}

export default CreateRequest;
